/* ==========================================================================
   DEMONTHA — cart enhancements
   --------------------------------------------------------------------------
   This file does NOT touch the theme's own cart flow:
     · cart.js (CartItems / CartRemoveButton) and cart-drawer.js
       (CartDrawerSection / CartDrawerItems) keep working exactly as before.
     · No theme custom element is redefined and no theme function is
       monkey-patched.
     · Every listener is delegated on `document`, so nothing has to be
       re-bound when the drawer is re-rendered with innerHTML.

   What it adds:
     1. An "Undo" notice after an item is removed
     2. Quantity changes announced to screen readers (aria-live)
     3. Floating chat bubbles hidden while the drawer is open (they overlap)
     4. Accessible open/close behaviour for the cross sell heading
     4b. In-card variant picking on cross sell cards (multi-variant products)
     5. Links inside a closed drawer taken out of the tab order
   ========================================================================== */

(function () {
  'use strict';

  if (window.__dmtCartInit) return;
  window.__dmtCartInit = true;

  var STRINGS = Object.assign(
    {
      removed: 'removed from your cart',
      undo: 'Undo',
      undone: 'The item is back in your cart.',
      undoError: 'We could not put the item back. Please try again.',
      dismiss: 'Dismiss notification',
      quantityUpdated: 'quantity updated',
      crossAdded: 'added to your cart',
      crossAddError: 'We could not add the item. Please try again.',
      crossPickOpen: 'options opened',
      crossPickClose: 'Close the picker'
    },
    window.dmtCartStrings || {}
  );

  var TOAST_TIMEOUT = 9000;
  var toast = null;
  var toastTimer = null;
  var pendingUndo = null;
  var liveRegion = null;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */

  function cartRoutes() {
    return (window.routes || (window.Shopify && window.Shopify.routes) || {});
  }

  function isCartPage() {
    var url = cartRoutes().cart_url;
    return !!url && window.location.pathname === url;
  }

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function requestConfig(body) {
    if (typeof window.fetchConfig === 'function') {
      var cfg = window.fetchConfig('javascript');
      cfg.headers['Content-Type'] = 'application/json';
      cfg.headers.Accept = 'application/json';
      cfg.body = body;
      return cfg;
    }
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body
    };
  }

  /* --------------------------------------------------- accessible announce */

  function ensureLiveRegion() {
    if (liveRegion && document.body.contains(liveRegion)) return liveRegion;
    liveRegion = document.createElement('p');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('data-dmt-cart-live', '');
    document.body.appendChild(liveRegion);
    return liveRegion;
  }

  function announce(message) {
    if (!message) return;
    var region = ensureLiveRegion();
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  /* ------------------------------------------------------------ undo toast */

  function toastHost() {
    /* When the drawer exists the notice is mounted inside <cart-drawer>, so
       cart-drawer.js's focus trap covers the "Undo" button and a keyboard
       user can reach it. The drawer re-renders the INSIDE of #CartDrawer,
       not the direct children of <cart-drawer>, so the notice survives a
       re-render. */
    return drawerEl() || document.body;
  }

  function buildToast() {
    if (toast && toast.isConnected) return toast;

    toast = document.createElement('div');
    toast.className = 'dmt-cart-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('data-open', 'false');

    var text = document.createElement('span');
    text.className = 'dmt-cart-toast__text';
    text.setAttribute('data-dmt-toast-text', '');

    var undoBtn = document.createElement('button');
    undoBtn.type = 'button';
    undoBtn.className = 'dmt-cart-toast__undo';
    undoBtn.textContent = STRINGS.undo;
    undoBtn.addEventListener('click', onUndoClick);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'dmt-cart-toast__close';
    closeBtn.setAttribute('aria-label', STRINGS.dismiss);
    closeBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    closeBtn.addEventListener('click', hideToast);

    toast.appendChild(text);
    toast.appendChild(undoBtn);
    toast.appendChild(closeBtn);
    toastHost().appendChild(toast);

    return toast;
  }

  function showToast(message) {
    var el = buildToast();
    var text = el.querySelector('[data-dmt-toast-text]');
    if (text) text.textContent = message;
    el.setAttribute('data-open', 'true');

    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(hideToast, TOAST_TIMEOUT);
  }

  function hideToast() {
    window.clearTimeout(toastTimer);
    if (toast) toast.setAttribute('data-open', 'false');
    pendingUndo = null;
  }

  function snapshotRemoval(button) {
    var variantId = button.getAttribute('data-dmt-variant');
    if (!variantId) return null;

    var quantity = parseInt(button.getAttribute('data-dmt-quantity'), 10);
    var properties = null;
    var raw = button.getAttribute('data-dmt-properties');

    if (raw && raw !== 'null' && raw !== '{}') {
      try {
        properties = JSON.parse(raw);
      } catch (e) {
        properties = null;
      }
    }

    return {
      id: variantId,
      quantity: quantity > 0 ? quantity : 1,
      title: button.getAttribute('data-dmt-title') || '',
      sellingPlan: button.getAttribute('data-dmt-selling-plan') || '',
      properties: properties
    };
  }

  function onRemoveClick(event) {
    var button = event.target.closest && event.target.closest('cart-remove-button');
    if (!button) return;

    var snapshot = snapshotRemoval(button);
    if (!snapshot) return;

    pendingUndo = snapshot;
    var label = snapshot.title
      ? '“' + snapshot.title + '” ' + STRINGS.removed
      : STRINGS.removed;
    showToast(label);
  }

  function onUndoClick() {
    if (!pendingUndo) {
      hideToast();
      return;
    }

    var item = pendingUndo;
    var routes = cartRoutes();
    if (!routes.cart_add_url) {
      hideToast();
      return;
    }

    var line = { id: item.id, quantity: item.quantity };
    if (item.properties && Object.keys(item.properties).length) line.properties = item.properties;
    if (item.sellingPlan) line.selling_plan = item.sellingPlan;

    pendingUndo = null;

    fetch(routes.cart_add_url, requestConfig(JSON.stringify({ items: [line] })))
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data && data.status && data.status !== 200) {
          announce(STRINGS.undoError);
          return;
        }
        hideToast();
        announce(STRINGS.undone);
        refreshCart();
      })
      .catch(function () {
        announce(STRINGS.undoError);
      });
  }

  /* ---------------------------------------------------------- cart refresh */

  function refreshCart() {
    var drawer = drawerEl();

    if (drawer) {
      /* The theme's own path: CartDrawerSection listens for
         'cart-drawer:refresh', fetches the sections and rewrites #CartDrawer.
         PUB_SUB cartUpdate is deliberately NOT published — the theme's
         CartDrawerItems.onCartUpdate replaces the drawer contents with the
         /cart page markup, which is a bug in the theme. */
      document.dispatchEvent(new CustomEvent('cart-drawer:refresh', { detail: { source: 'dmt-cart' } }));
      window.setTimeout(syncTabindex, 350);
      return;
    }

    refreshCartPageSections();
  }

  function refreshCartPageSections() {
    var itemsEl = document.getElementById('main-cart-items');
    var footerEl = document.getElementById('main-cart-footer');

    /* main-cart-footer is not printed at all when the cart is empty. A full
       reload is the safe answer in that case. */
    if (!itemsEl || !itemsEl.dataset.id || !footerEl || !footerEl.dataset.id) {
      window.location.reload();
      return;
    }

    var targets = [
      { section: itemsEl.dataset.id, root: itemsEl, selector: '.js-contents' },
      { section: footerEl.dataset.id, root: footerEl, selector: '.js-contents' }
    ];

    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) targets.push({ section: 'cart-icon-bubble', root: bubble, selector: '.shopify-section' });

    var query = targets
      .map(function (t) {
        return t.section;
      })
      .join(',');

    fetch(window.location.pathname + '?sections=' + encodeURIComponent(query))
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        targets.forEach(function (t) {
          var html = data[t.section];
          if (!html) return;
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var source = parsed.querySelector(t.selector);
          var destination = t.root.querySelector(t.selector) || t.root;
          if (source && destination) destination.innerHTML = source.innerHTML;
        });
      })
      .catch(function () {
        window.location.reload();
      });
  }

  /* -------------------------------------------- quantity change announcing */

  function onQuantityChange(event) {
    var input = event.target;
    if (!input || !input.classList || !input.classList.contains('js-counter-quantity')) return;

    var title = input.getAttribute('data-dmt-title') || '';
    var value = input.value;
    announce((title ? '“' + title + '” ' : '') + STRINGS.quantityUpdated + ': ' + value);
  }

  /* ------------------------------------- floating chat bubble / overlap fix */

  var EXTRA_FLOAT_SELECTORS = [
    '[class*="whatsapp" i]',
    '[id*="whatsapp" i]',
    '[class*="wa-widget" i]',
    '[id*="wa-widget" i]',
    'iframe[src*="whatsapp" i]'
  ];

  var hiddenFloats = [];

  function collectFloatingWidgets() {
    var found = [];

    function addFixedAncestor(node) {
      var el = node;
      for (var i = 0; i < 6 && el && el !== document.body; i++) {
        var position = window.getComputedStyle(el).position;
        if (position === 'fixed' || position === 'sticky') {
          if (found.indexOf(el) === -1) found.push(el);
          return;
        }
        el = el.parentElement;
      }
    }

    document
      .querySelectorAll('a[href*="wa.me"], a[href*="api.whatsapp.com"], a[href^="whatsapp:"]')
      .forEach(addFixedAncestor);

    EXTRA_FLOAT_SELECTORS.forEach(function (selector) {
      var nodes;
      try {
        nodes = document.querySelectorAll(selector);
      } catch (e) {
        return;
      }
      nodes.forEach(function (el) {
        if (el.closest('cart-drawer')) return;
        if (window.getComputedStyle(el).position !== 'fixed') return;
        if (found.indexOf(el) === -1) found.push(el);
      });
    });

    return found;
  }

  function hideFloatingWidgets() {
    if (hiddenFloats.length) return;
    hiddenFloats = collectFloatingWidgets().map(function (el) {
      var previous = el.style.visibility;
      el.classList.add('dmt-cart-float-hidden');
      el.style.visibility = 'hidden';
      el.setAttribute('aria-hidden', 'true');
      return { el: el, previous: previous };
    });
  }

  function restoreFloatingWidgets() {
    hiddenFloats.forEach(function (entry) {
      entry.el.style.visibility = entry.previous || '';
      entry.el.classList.remove('dmt-cart-float-hidden');
      entry.el.removeAttribute('aria-hidden');
    });
    hiddenFloats = [];
  }

  /* ------------------------------------------------------------- tab order */

  function syncTabindex() {
    var drawer = drawerEl();
    if (!drawer) return;

    var selectors = drawer.getAttribute('data-toggle-tabindex');
    if (!selectors) return;

    var value = drawer.hasAttribute('open') ? '0' : '-1';
    var nodes;
    try {
      nodes = drawer.querySelectorAll(selectors);
    } catch (e) {
      return;
    }
    nodes.forEach(function (el) {
      el.setAttribute('tabindex', value);
    });
  }

  /* ------------------------------------------------ cross sell open/close */

  function onToggleClick(event) {
    var button = event.target.closest && event.target.closest('[data-dmt-toggle="cross-sell"]');
    if (!button) return;

    event.preventDefault();
    var block = button.closest('.wt-cart__cross-sell');
    if (!block) return;

    var collapsed = block.classList.toggle('wt-cart__cross-sell--collapsed');
    button.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
  }

  /* ------------------------------------- cross sell: add to cart directly

     THE MEASURED BUG (live preview): the card's bag icon used to sit inside
     the theme's <quick-add> custom element. On click quick-add.js opened
     `.wt__quick-buy__container`, which is position:fixed with z-index 100,
     while the cart drawer is position:fixed with z-index 100000 AND THEIR
     RECTANGLES ARE IDENTICAL (1420,0,500,1009). So the panel opened but
     stayed underneath the drawer. Measured: cart 1 -> 1 (nothing added),
     nothing changed on screen, `quick-buy-page-overlay` was added to body,
     focus left the drawer for the invisible panel's .close-btn, and there
     were zero console errors. What the customer saw: "I click and nothing
     happens."

     THE FIX: the quick-buy panel is out of the picture. The button goes
     straight to /cart/add.js and refreshes the drawer in place
     (refreshCart) — the drawer does NOT close. */

  /* After adding, the drawer rewrites the inside of #CartDrawer with
     innerHTML: the button that was pressed is removed from the DOM and focus
     falls to <body>. A mouse user never notices, but a keyboard user loses
     their place and the next Tab starts from the top of the document.

     Focus is only restored when the action came from the KEYBOARD. The test
     is `event.detail`: a click generated by Enter/Space reports 0, a real
     mouse click reports 1 or more. Forcibly moving a mouse user's focus
     would be an unwanted jump.

     A fixed setTimeout is NOT enough: refreshCart fetches the sections and
     how long the re-render takes depends on the network. A single 500ms shot
     was measured to fire too early about a quarter of the time (focus stayed
     on <body>). Instead we wait for the old button to leave the DOM, which
     is the definitive sign that the re-render finished, and give up after
     2 seconds. */
  function restoreCrossFocus(oldButton, index) {
    var waited = 0;
    var timer = window.setInterval(function () {
      waited += 100;
      var reRendered = !document.body.contains(oldButton);
      if (!reRendered && waited < 2000) return;
      window.clearInterval(timer);

      var root = document.querySelector('[data-dmt-xsell]');
      if (!root) return;
      /* The index counts CARD level buttons (`[data-dmt-cross-cta]`), not the
         variant buttons inside a picker: one card can hold 22 options and
         counting `[data-dmt-xsell-add]` would shift the index completely. */
      var buttons = root.querySelectorAll('[data-dmt-cross-cta]');
      var target = buttons[Math.min(index, buttons.length - 1)];
      if (!target) target = root.querySelector('.dmt-cart__cross-link');
      if (target && typeof target.focus === 'function') target.focus();
    }, 100);
  }

  function onCrossAddClick(event) {
    var button = event.target.closest && event.target.closest('[data-dmt-xsell-add]');
    if (!button) return;

    var viaKeyboard = event.detail === 0;
    var root = button.closest('[data-dmt-xsell]');
    /* Focus is restored at card level: when the add came from a picker the
       picker closes, and the place to return to is that card's bag icon. */
    var card = button.closest('.dmt-cart__cross-card');
    var cta = (card && card.querySelector('[data-dmt-cross-cta]')) || button;
    var index = root
      ? Array.prototype.indexOf.call(root.querySelectorAll('[data-dmt-cross-cta]'), cta)
      : 0;
    if (index < 0) index = 0;

    /* The button is the SIBLING of the stretched link covering the card, so
       the click never passes through the link and there is no navigation to
       cancel. preventDefault only stops the <button>'s own default. */
    event.preventDefault();

    if (button.getAttribute('aria-busy') === 'true') return;

    var variantId = button.getAttribute('data-dmt-variant');
    var title = button.getAttribute('data-dmt-title') || '';
    var routes = cartRoutes();
    if (!variantId || !routes.cart_add_url) return;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    function release() {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }

    fetch(routes.cart_add_url, requestConfig(JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })))
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data && data.status && data.status !== 200) {
          release();
          announce(STRINGS.crossAddError);
          return;
        }
        /* Short visual confirmation. The refresh drops the card out of the
           list straight away ("already in the cart" filter), but until the
           refresh lands the customer needs to see that something happened. */
        /* If the add came from a picker the picker closes, so the pressed
           button is no longer visible and the tick is shown on the card's
           bag icon instead. */
        var panel = button.closest('[data-dmt-pick-panel]');
        if (panel) {
          closePicker(panel, false);
          cta.setAttribute('data-dmt-added', 'true');
        } else {
          button.setAttribute('data-dmt-added', 'true');
        }
        announce((title ? '“' + title + '” ' : '') + STRINGS.crossAdded);
        refreshCart();
        if (viaKeyboard) restoreCrossFocus(button, index);
        window.setTimeout(release, 1200);
      })
      .catch(function () {
        release();
        announce(STRINGS.crossAddError);
      });
  }

  /* --------------------------- cross sell: in-card variant picking

     WHY: the bag icon used to be printed only for SINGLE VARIANT products,
     and much of the catalogue is multi-variant, so some cards had the icon
     and some did not. That read as a bug. Sending the customer to the
     product page is not a fix: it drops them out of the flow at the moment
     they are closest to paying.

     THE FIX: every eligible card has the icon. On a multi-variant product the
     click does not add anything, it opens a picker INSIDE the card; once an
     option is chosen the add goes through the same onCrossAddClick path. No
     navigation, no leaving the page.

     NO FOCUS TRAP IS SET (deliberately): the picker is an ordinary box
     inside the card, not a modal. Tab moves on naturally, Esc closes it and
     returns focus to the button that opened it. The old <quick-add> route,
     with its invisible panel and Tab prison, was NOT reinstated. */

  function pickPanelOf(toggle) {
    var id = toggle.getAttribute('data-dmt-pick-target');
    return id ? document.getElementById(id) : null;
  }

  function pickToggleOf(panel) {
    if (!panel || !panel.id) return null;
    return document.querySelector('[data-dmt-pick-target="' + panel.id + '"]');
  }

  function closePicker(panel, refocus) {
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    var toggle = pickToggleOf(panel);
    if (!toggle) return;
    toggle.setAttribute('aria-expanded', 'false');
    if (refocus && typeof toggle.focus === 'function') toggle.focus();
  }

  function closeAllPickers(except) {
    var panels = document.querySelectorAll('[data-dmt-pick-panel]');
    Array.prototype.forEach.call(panels, function (panel) {
      if (panel !== except) closePicker(panel, false);
    });
  }

  function onCrossPickClick(event) {
    if (!event.target.closest) return;

    var toggle = event.target.closest('[data-dmt-xsell-pick]');
    if (toggle) {
      event.preventDefault();
      var panel = pickPanelOf(toggle);
      if (!panel) return;
      if (!panel.hidden) {
        closePicker(panel, true);
        return;
      }
      closeAllPickers(panel);
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      announce((toggle.getAttribute('data-dmt-title') || '') + ' ' + STRINGS.crossPickOpen);
      /* Focus goes to the first SELECTABLE option: a sold out variant is
         `disabled`, and focusing it would strand a keyboard user. */
      var first = panel.querySelector('[data-dmt-pick-opt]:not([disabled])');
      if (first && typeof first.focus === 'function') first.focus();
      return;
    }

    var closeBtn = event.target.closest('[data-dmt-pick-close]');
    if (closeBtn) {
      event.preventDefault();
      closePicker(closeBtn.closest('[data-dmt-pick-panel]'), true);
      return;
    }

    /* A click outside closes the picker. Clicks INSIDE it, including the
       variant buttons, are exempt. Focus is not moved: yanking a mouse
       user's focus because they clicked elsewhere would be wrong. */
    if (!event.target.closest('[data-dmt-pick-panel]')) closeAllPickers(null);
  }

  function onPickKeydown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    if (!event.target.closest) return;

    var panel = event.target.closest('[data-dmt-pick-panel]');
    if (!panel) {
      var toggle = event.target.closest('[data-dmt-xsell-pick]');
      panel = toggle ? pickPanelOf(toggle) : null;
    }
    if (!panel || panel.hidden) return;

    /* The drawer has its own Esc listener that closes the drawer. While a
       picker is open Esc has to close the PICKER first, which is why this
       listens in the capture phase and stops the event here. */
    event.stopPropagation();
    event.preventDefault();
    closePicker(panel, true);
  }

  /* ------------------------------ cross sell: Shopify recommendation layer

     The server has already printed the RULE BASED list from
     snippets/cart-cross-sell.liquid, so there is no gap on screen. What
     happens here is only an upgrade attempt: if the merchant curated a
     "complementary" recommendation in Search & Discovery, that list is
     better and replaces it.

     An empty or failed response is ignored silently and the rule based list
     stays. At most one request is made per anchor product; re-rendering the
     drawer does not re-request the same anchor. */

  var XSELL_SECTION = 'dmt-cart-cross-sell';
  var xsellTried = {};

  function upgradeCrossSell() {
    var root = document.querySelector('[data-dmt-xsell]');
    if (!root) return;

    var source = root.getAttribute('data-dmt-xsell-source');
    /* manual = the merchant picked the products by hand (P0), never overridden.
       recommendations = already upgraded. */
    if (source === 'manual' || source === 'recommendations') return;

    var anchor = root.getAttribute('data-dmt-xsell-anchor');
    if (!anchor) return;

    /* The cache records what the anchor RESULTED IN, not merely that a
       request was made: the drawer is reprinted on every cart update and the
       server keeps returning the rule based list, so there is no point
       re-requesting an anchor that already came back empty. */
    if (xsellTried[anchor] === 'pending' || xsellTried[anchor] === 'empty') return;

    var base = cartRoutes().product_recommendations_url || '/recommendations/products';
    var url =
      base +
      (base.indexOf('?') === -1 ? '?' : '&') +
      'section_id=' + encodeURIComponent(XSELL_SECTION) +
      '&product_id=' + encodeURIComponent(anchor) +
      '&intent=complementary&limit=10';

    xsellTried[anchor] = 'pending';

    fetch(url)
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (html) {
        if (!html) {
          xsellTried[anchor] = 'empty';
          return;
        }
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-dmt-xsell]');
        /* If the recommendation list was empty the snippet falls back to the
           rule table; the source is then not "recommendations" and there is
           nothing worth swapping in. */
        if (!fresh || fresh.getAttribute('data-dmt-xsell-source') !== 'recommendations') {
          xsellTried[anchor] = 'empty';
          return;
        }
        var current = document.querySelector('[data-dmt-xsell]');
        if (!current || !current.parentNode) {
          xsellTried[anchor] = 'empty';
          return;
        }
        current.parentNode.replaceChild(fresh, current);
        xsellTried[anchor] = 'done';
        syncTabindex();
      })
      .catch(function () {
        xsellTried[anchor] = 'empty';
      });
  }

  /* ----------------------------------------------------------------- init */

  function observeDrawerState() {
    var body = document.body;
    if (!body || typeof MutationObserver !== 'function') return;

    var wasOpen = body.classList.contains('page-overlay-cart-on');

    new MutationObserver(function () {
      var isOpen = body.classList.contains('page-overlay-cart-on');
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;

      if (isOpen) {
        hideFloatingWidgets();
      } else {
        restoreFloatingWidgets();
        hideToast();
        /* A picker left open while the drawer closes would keep its buttons
           in the tab order even though nothing is visible. */
        closeAllPickers(null);
      }
      syncTabindex();
    }).observe(body, { attributes: true, attributeFilter: ['class'] });

    var drawer = drawerEl();
    if (drawer) {
      var pending = null;
      new MutationObserver(function () {
        window.clearTimeout(pending);
        pending = window.setTimeout(function () {
          syncTabindex();
          upgradeCrossSell();
        }, 120);
      }).observe(drawer, { childList: true, subtree: true });
    }
  }

  function init() {
    /* The remove button has to be read BEFORE the theme's listener (capture)
       so the variant and quantity are captured before the row leaves the
       DOM. */
    document.addEventListener('click', onRemoveClick, true);
    document.addEventListener('click', onToggleClick);
    /* Delegated: the drawer is reprinted with innerHTML on every cart update,
       so binding to each card button would create a re-binding problem —
       another fragility of the old <quick-add> route. */
    document.addEventListener('click', onCrossAddClick);
    /* ORDER MATTERS: onCrossAddClick runs first. The "click outside" branch
       of onCrossPickClick exempts the variant buttons inside the picker, so
       adding and closing do not cut each other off. */
    document.addEventListener('click', onCrossPickClick);
    /* Capture: this must run BEFORE the drawer's own Esc listener. */
    document.addEventListener('keydown', onPickKeydown, true);
    document.addEventListener('change', onQuantityChange);

    if (isCartPage()) {
      /* There is no drawer on /cart, so no floating bubble to hide; the CSS
         leaves clearance under the checkout button instead. */
      return;
    }

    observeDrawerState();
    syncTabindex();
    upgradeCrossSell();

    if (reduceMotion) {
      document.documentElement.setAttribute('data-dmt-reduce-motion', 'true');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
